import React, { useState, useEffect, useRef } from 'react';
import { View, Dimensions, Text } from 'react-native';
import { connect } from 'react-redux';
import IdleTimerManager from 'react-native-idle-timer';
import i18n from 'i18n-js';

import {
  isWeb,
  Alert,
  Popup,
  timePeriod,
  formatStartOf,
  machineDetailsConditions,
  workerType,
  workerTypeStatusNames,
  localTime,
  ALL_MACHINES,
  isPhone,
  chartStatuses,
  addQueryParams,
} from '../../helpers';
import { scale, height, fullHeight, fullWidth } from '@components/frame';
import { FeatureFlagsAvailable } from '@components/featureFlags';

import {
  getMachines,
  getMachineStatistics,
  setSelectedMachine,
  updateMachineInfo,
  clearMachines,
  setNCFileStatistics,
  setMachineStatistics,
  setSelectedTime,
} from '../../reducers/machines';
import { updateDevice } from '@reducers/device';
import { removeProductionInformation } from '@reducers/manufacture';
import { generateImageURL } from '@reducers/blobStorage';
import { getTimeSetup } from '@reducers/time';
import { generateTokenInfo } from '@reducers/user';
import { getConnectorInfo } from '@reducers/connector';
import { setGlobalSelectedLevel } from '@reducers/globalAdd';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import TouchableElement from '@components/common/touchableElement';
import Refresh from '@components/common/Refresh';
import Loading from '@components/loading';
import AddMachine from '@components/settings/v2/structure/machines/machines/addMachine';
import List from './components/list';
import Details from './components/details';
import MissingPIModal from './components/modals/missingPIModal';
import MachineActionsModal from './components/modals/machineActionsModal';
import MachinesSelectModal from './components/modals/machinesSelectModal';
import ConnectorOff from '@components/settings/components/common/connectorOff';
import NoConnector from '@components/settings/components/common/noConnector';
import { LIST_WIDTH } from '@v2/pages/common/constants/base.constants';

let interval;
let initialLoadingGlobal = true;

const MODES = {
  FULL: 'FULL',
  LIST: 'LIST',
  DETAILS: 'DETAILS',
};

const TRACKED_TIME_PERIODS = [timePeriod.DAY, timePeriod.WEEK, timePeriod.MONTH, timePeriod.YEAR];

const Machines = ({
  getMachines,
  setSelectedMachine,
  updateMachineInfo,
  machines: { machines, machinesLoaded, selectedItem, selectedTime, isError, machineStatistics },
  dynamicConfig: { machineDetailsLevel },
  machines: machinesStorage,
  navigation,
  C,
  updateAppValue,
  time,
  removeProductionInformation,
  mode,
  panelViewMode,
  fullWebWidth,
  onBack,
  route,
  lg,
  connector,
  generateImageURL,
  updateAppView,
  getTimeSetup,
  getConnectorInfo,
  setGlobalSelectedLevel,
  setNCFileStatistics,
  setMachineStatistics,
  setSelectedTime,
  machineListOEE,
  featureFlags,
}) => {
  const s = scale;

  const globalScroll = useRef();

  const initialLevel = route?.params?.level || machineDetailsLevel || 'ncFile';
  const initialTime = route?.params?.section || timePeriod.NOW;
  const selectedRoute = route?.params?.selected;
  const d = route?.params?.date;
  const initialChartDate = d ? localTime(d).format() : null;
  const dEnd = route?.params?.dateEnd;
  const initialChartDateEnd = dEnd ? localTime(dEnd).format() : null;
  const noMachine = route?.params?.noMachine && route?.params?.noMachine !== 'false';
  const [initialLoading, setInitialLoading] = useState(initialLoadingGlobal);

  const [modalMissingPIOpen, setModalMissingPIOpen] = useState(false);
  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  const [isMachinesSelectModalOpen, setIsMachinesSelectModalOpen] = useState(false);
  const [isRemovingPI, setIsRemovingPI] = useState(false);

  const isGlobalMachineList = mode === MODES.LIST && isPhone;
  const allSelected = selectedItem?.id === ALL_MACHINES || isGlobalMachineList;

  const [selectedLevel, setSelectedLevelInternal] = useState(initialLevel);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const setSelectedLevel = (data) => {
    addQueryParams({ level: data });
    setSelectedLevelInternal(data);
    setGlobalSelectedLevel(data);
  };

  useEffect(() => {
    if (machineDetailsLevel) {
      setSelectedLevel(machineDetailsLevel);
    }
  }, [machineDetailsLevel]);

  const [selectedTimeCounter, setSelectedTimeCounter] = useState(0);

  const [chartData, setChartData] = useState({});
  const [selectedChartDate, setSelectedChartDateInternal] = useState(initialChartDate);
  const [selectedChartDateEnd, setSelectedChartDateEndInternal] = useState(initialChartDateEnd);

  const lastSelectedChartDateRef = useRef(
    TRACKED_TIME_PERIODS.reduce((acc, period) => {
      acc[period] = null;
      return acc;
    }, {})
  );

  useEffect(() => {
    if (initialChartDate && TRACKED_TIME_PERIODS.includes(initialTime)) {
      lastSelectedChartDateRef.current[initialTime] = localTime(initialChartDate).startOf(formatStartOf(initialTime)).format();
    }
  }, [initialChartDate, initialTime]);

  useEffect(() => {
    if (selectedChartDate && TRACKED_TIME_PERIODS.includes(selectedTime)) {
      lastSelectedChartDateRef.current[selectedTime] = selectedChartDate;
    }
  }, [selectedChartDate, selectedTime]);

  useEffect(() => {
    if (!TRACKED_TIME_PERIODS.includes(selectedTime)) {
      return;
    }

    if (!selectedChartDate) {
      if (selectedChartDateEnd) {
        setSelectedChartDateEnd(null);
      }
      return;
    }

    const expectedEnd = localTime(selectedChartDate).clone().add(1, selectedTime).startOf(formatStartOf(selectedTime)).format();

    if (selectedChartDateEnd !== expectedEnd) {
      setSelectedChartDateEnd(expectedEnd);
    }
  }, [selectedChartDate, selectedChartDateEnd, selectedTime]);

  const setSelectedChartDate = (data) => {
    const formattedDate = data ? localTime(data).format() : null;
    addQueryParams({ date: formattedDate });
    setSelectedChartDateInternal(formattedDate);
  };

  const setSelectedChartDateEnd = (data) => {
    const formattedDateEnd = data ? localTime(data).format() : null;
    addQueryParams({ dateEnd: formattedDateEnd });
    setSelectedChartDateEndInternal(formattedDateEnd);
  };

  const [selectedPeriod, setSelectedPeriodInternal] = useState(chartStatuses[String(route?.params?.status).toLocaleLowerCase()] || 'Productive');

  const setSelectedPeriod = (data) => {
    if (globalScroll.current) {
      globalScroll.current?.scrollTo({ x: 0, y: 0, animated: false });
    }
    addQueryParams({ status: data });
    setSelectedPeriodInternal(data);
  };

  const setSelectedTimeFunc = (t) => {
    if (globalScroll.current) {
      globalScroll.current?.scrollTo({ x: 0, y: 0, animated: false });
    }
    if (
      selectedPeriod === chartStatuses.timeline &&
      ((t === timePeriod.MONTH && !FeatureFlagsAvailable('timeline-month', featureFlags)) ||
        (t === timePeriod.YEAR && !FeatureFlagsAvailable('timeline-year', featureFlags)))
    ) {
      setSelectedPeriod(chartStatuses.productive);
    }
    // Reset date to correct start-of-period for current day when switching tabs
    // This prevents using stale dates from other tabs (e.g., "start of week" when switching to day view)
    const isTrackedPeriod = TRACKED_TIME_PERIODS.includes(t);
    const isSamePeriod = t === selectedTime;
    let targetDate = selectedChartDate;
    let targetDateEnd = selectedChartDateEnd;

    if (isTrackedPeriod) {
      const cachedDate = lastSelectedChartDateRef.current[t];
      if (!isSamePeriod || !targetDate) {
        const baseMoment = selectedChartDate ? localTime(selectedChartDate) : localTime();
        targetDate = cachedDate || baseMoment.startOf(formatStartOf(t)).format();
      }

      targetDate = localTime(targetDate).startOf(formatStartOf(t)).format();
      targetDateEnd = localTime(targetDate).clone().add(1, t).startOf(formatStartOf(t)).format();
      lastSelectedChartDateRef.current[t] = targetDate;

      if (selectedChartDate !== targetDate) {
        setSelectedChartDateInternal(targetDate);
      }

      if (selectedChartDateEnd !== targetDateEnd) {
        setSelectedChartDateEndInternal(targetDateEnd);
      }

      addQueryParams({ section: t, date: targetDate, dateEnd: targetDateEnd });
    } else {
      addQueryParams({ section: t });
    }
    setChartData({});
    setNCFileStatistics({ data: [], page: 1 });
    setMachineStatistics({
      data: [],
    });
    setSelectedTime(t);
    // Always increment counter to force EventCharts to re-render with fresh state
    setSelectedTimeCounter((counter) => counter + 1);
  };

  const triggerRemovePI = async () => {
    setActionsModalOpen(false);
    Alert({
      title: i18n.t('Are you sure?'),
      text: `${i18n.t('You are about to remove all existing production information.')}\n\n${i18n.t(
        'After removing, you can add new production information or mark the nc file as Ignored.'
      )}`,
      actions: [
        {
          text: i18n.t('Cancel'),
          onPress: () => {},
          style: 'primary',
        },
        {
          text: i18n.t('Remove'),
          style: 'danger',
          onPress: async () => {
            setIsRemovingPI(true);
            console.log(selectedItem, 'selectedItem');
            await removeProductionInformation({
              ncFileId: selectedItem.machineStatus.ncFileId,
              productionBatchId: selectedItem.machineStatus.productionBatchId,
            });
            setIsRemovingPI(false);
          },
        },
      ],
    });
  };

  const {
    server: { offset = 0 },
  } = time;

  const fetchData = async () => {
    await getMachines({});
    await getTimeSetup();

    await getConnectorInfo();
  };

  useEffect(() => {
    setGlobalSelectedLevel(selectedLevel);
    setSelectedTime(isPhone ? initialTime : allSelected && initialTime === timePeriod.NOW ? timePeriod.DAY : initialTime);
  }, []);

  useEffect(() => {
    if (noMachine) {
      setInitialLoading(false);
      initialLoadingGlobal = false;
      return;
    }

    (async () => {
      await getMachines(false, selectedRoute);
      setInitialLoading(false);
      initialLoadingGlobal = false;
    })();

    const visibilitychange = async () => {
      if (!window.document.hidden) {
        await fetchData();
      }
    };
    if (!isWeb) {
      IdleTimerManager.setIdleTimerDisabled(true);
    } else {
      window.document.addEventListener('visibilitychange', visibilitychange);
    }

    interval = setInterval(() => {
      updateMachineInfo();
    }, 1000 * 5);

    return () => {
      clearInterval(interval);
      if (!isWeb) {
        IdleTimerManager.setIdleTimerDisabled(false);
      } else {
        window.document.removeEventListener('visibilitychange', visibilitychange);
      }
    };
  }, []);

  if (!connector.loaded) {
    return <Loading />;
  }

  const machineConnector = connector.connectorDetails?.status?.find((c) => c.workerType === workerType.MACHINES) || {};

  if (connector.loaded && !connector.connectorDetails?.connectorId && !machines.length && machinesLoaded) {
    return (
      <View style={{ backgroundColor: C('Background/Light/System'), flex: 1 }}>
        <NoConnector time={machineConnector?.lastAliveEventReceivedOn} />
      </View>
    );
  }

  if (connector.loaded && machineConnector?.status !== workerTypeStatusNames.Online && !machines.length && machinesLoaded) {
    if (machineConnector?.status == workerTypeStatusNames.Onboarding) {
      return (
        <View
          style={{
            backgroundColor: C('Background/Light/System'),
            flex: 1,
            justifyContent: 'center',
            alignContent: 'center',
            alignItems: 'center',
          }}
        >
          <Text style={[font['Large Title'], { color: C('Label/Light/Primary'), textAlign: 'center', marginBottom: scale(28) }]}>
            {i18n.t('Connecting...')}
          </Text>
          <Svg icon="connecting" width={scale(88)} height={scale(88)} color="Gray/Light/2" />
        </View>
      );
    }

    return (
      <View style={{ backgroundColor: C('Background/Light/System'), flex: 1 }}>
        <ConnectorOff time={machineConnector?.lastAliveEventReceivedOn} localHost={connector.connectorDetails?.localHost} />
      </View>
    );
  }

  if (isError && !noMachine) {
    return <Refresh onPress={fetchData} />;
  }

  if (!machinesLoaded && !noMachine) {
    return <Loading />;
  }

  if (!machines.length && !noMachine) {
    return (
      <View
        style={{
          width: '100%',
          height: panelViewMode ? fullHeight : height,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: C('Background/Light/System'),
        }}
      >
        <TouchableElement
          onPress={() =>
            Popup({
              Component: AddMachine,
              width: 580,
            })
          }
        >
          {({ boxStyle, pressHoverColor }) => (
            <View
              style={[
                boxStyle,
                {
                  width: s(324),
                  height: s(112),
                  borderRadius: s(12),
                  backgroundColor: C(pressHoverColor('Button/Light/Primary')),
                },
              ]}
            >
              <View style={{ width: '100%', alignItems: 'center', marginTop: s(23) }}>
                <View
                  style={{
                    backgroundColor: C('Label/Light/AlwaysLight Primary'),
                    width: s(27),
                    height: s(27),
                    borderRadius: s(27) / 2,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Svg icon="plus" width={s(18)} height={s(18)} color={'Button/Light/Primary'} />
                </View>
              </View>
              <View style={{ width: '100%', alignItems: 'center', marginTop: s(14) }}>
                <Text style={[font['Title 2'], { color: C('Button/Light/Primary Text') }]}>{i18n.t('Add Machine')}</Text>
              </View>
            </View>
          )}
        </TouchableElement>
      </View>
    );
  }

  const listWidth = s(LIST_WIDTH);
  const detailsWidth = fullWidth - s(LIST_WIDTH);

  const detailsProps = {
    selectedTime,
    setSelectedTime: setSelectedTimeFunc,
    setModalMissingPIOpen,
    updateAppValue,
    navigation,
    detailsWidth,
    setActionsModalOpen,
    isRemovingPI,
    triggerRemovePI,
    setSelectedLevel,
    modalMissingPIOpen,
    panelViewMode,
    selectedLevel,
    route,
    highlightHeader: route?.params?.highlightHeader,
    noMachine,
    lg,
    selectedChartDate,
    selectedChartDateEnd,
    setSelectedChartDate,
    setSelectedChartDateEnd,
    onBack,
    machinesLoaded,
    initialLoading,
    selectedPeriod,
    setSelectedPeriod,
    chartData,
    setChartData,
    globalScroll,
    selectedTimeCounter,
    setIsMachinesSelectModalOpen,
  };

  const listProps = {
    data: machines,
    machinesLoaded,
    machines: machinesStorage,
    setSelectedMachine,
    selectedItem,
    timeOffset: offset,
    navigation,
    updateAppValue,
    listWidth,
    selectedTime,
    setSelectedTime: setSelectedTimeFunc,
    generateImageURL,
    initialLoading,
    selectedPeriod,
    machineStatistics,
    setSelectedPeriod,
    machineListOEE,
    selectedGroup,
    setSelectedGroup,
  };

  const renderComponent = () => {
    switch (mode) {
      case MODES.DETAILS: {
        return <Details {...detailsProps} />;
      }
      case MODES.LIST: {
        return isPhone && selectedTime !== timePeriod.NOW ? <Details {...{ ...detailsProps, isGlobalMachineList }} /> : <List {...listProps} />;
      }
      case MODES.FULL: {
        return [
          <List key="ListComponent" {...listProps} />,
          <View key="DetailsComponent" style={{ alignItems: 'center', flex: 1 }}>
            <Details {...detailsProps} />
          </View>,
        ];
      }
    }
  };

  const { status, ncFileStatus, subStatus, ncFile } = selectedItem?.machineStatus || {};

  const { machineActive, ignoredNcFile } = machineDetailsConditions({
    status,
    ncFileStatus,
    subStatus,
    ncFileExist: !!ncFile,
  });

  return [
    <View
      key="machineDetailsBlock"
      style={[
        {
          width: '100%',
          height: panelViewMode ? fullHeight : '100%',
          backgroundColor: C('Background/Light/Detail'),
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          flex: 1,
        },
      ]}
    >
      <View style={{ width: '100%', flex: 1 }}>
        <View style={{ flexDirection: 'row', flex: 1 }}>{renderComponent()}</View>
      </View>
      <MissingPIModal {...{ setModalMissingPIOpen, modalMissingPIOpen }} key="MissingPIModalGlobal" />
      {actionsModalOpen && (
        <MachineActionsModal
          {...{
            actionsModalOpen,
            panelViewMode,
            machineActive,
            selectedLevel,
            setActionsModalOpen,
            selectedItem,
            ignoredNcFile,
            selectedTime,
            triggerRemovePI,
            isRemovingPI,
          }}
          key="MachineActionsModal"
        />
      )}
      {isMachinesSelectModalOpen && (
        <MachinesSelectModal {...{ C, machines, selectedMachine: selectedItem, setSelectedMachine, setIsMachinesSelectModalOpen, panelViewMode }} />
      )}
    </View>,
  ];
};

const mapStateToProps = ({ machines, time, connector, dynamicConfig, statistics: { machineListOEE }, user: { featureFlags } }) => ({
  machines,
  time,
  connector,
  dynamicConfig,
  machineListOEE,
  featureFlags,
});

export default connect(mapStateToProps, {
  getMachines,
  setSelectedMachine,
  setSelectedTime,
  updateMachineInfo,
  updateDevice,
  removeProductionInformation,
  clearMachines,
  generateImageURL,
  getTimeSetup,
  getConnectorInfo,
  setGlobalSelectedLevel,
  getMachineStatistics,
  setNCFileStatistics,
  setMachineStatistics,
})(ColorWrapper(Machines));
